import { TRANSLATIONS, Language } from '../i18n/translations';

/**
 * Utilidades de formateo de moneda y gestión de errores de ofertas en el mercado.
 */

/**
 * Diccionario de literales traducidos por defecto según el failure.code del backend.
 */
export const MARKET_OFFER_CODE_LITERALS: Record<string, string> = {
  FUNDS_RESERVATION_REJECTED: 'Reserva de fondos rechazada. Saldo insuficiente para presentar la oferta.',
  INSUFFICIENT_FUNDS: 'Saldo insuficiente para presentar la oferta.',
};

/**
 * Obtiene el idioma actualmente seleccionado por el usuario en la aplicación.
 */
function getActiveLanguage(): Language {
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('fm_language') as Language;
      if (stored && ['es', 'ca', 'gl', 'eu'].includes(stored)) {
        return stored;
      }
    }
  } catch {}
  return 'es';
}

/**
 * Resuelve un literal en el diccionario de idiomas con interpolación opcional.
 */
function translateLiteral(key: string, params?: Record<string, string | number>): string | null {
  const lang = getActiveLanguage();
  const dict = TRANSLATIONS[lang] || TRANSLATIONS['es'];
  let text = dict[key] ?? TRANSLATIONS['es']?.[key];
  if (!text) return null;

  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      text = text!.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    });
  }

  return text;
}

/**
 * Formatea un importe numérico a moneda EUR según el formato utilizado en la aplicación.
 * Ejemplo: 18000000 -> 18.000.000,00 €
 */
export function formatCurrency(amount: number | string | null | undefined): string {
  if (amount === undefined || amount === null || amount === '') {
    return '0,00 €';
  }
  const num = typeof amount === 'number' ? amount : Number(amount);
  if (isNaN(num)) return '0,00 €';

  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
    .format(num)
    .replace(/\u00A0/g, ' ');
}

/**
 * Extrae y traduce el mensaje de error de una oferta basándose estrictamente en failure.code.
 * - Si failure.code === 'FUNDS_RESERVATION_REJECTED' o 'INSUFFICIENT_FUNDS':
 *   - Con importes: «Saldo insuficiente para presentar la oferta. Necesitas {requestedAmount} y tienes {availableAmount} disponibles.»
 *   - Sin importes: se muestra el literal configurado para dicho código (ej. «Reserva de fondos rechazada. Saldo insuficiente para presentar la oferta.»).
 * - Para otros códigos, consulta si existe un literal específico para el código antes de recurrir al mensaje genérico.
 */
export function getMarketOfferErrorMessage(err: any): string {
  const failure = err?.failure || err?.responseBody?.failure;
  const code = failure?.code;

  if (code === 'INSUFFICIENT_FUNDS' || code === 'FUNDS_RESERVATION_REJECTED') {
    const hasRequested = failure?.requestedAmount !== undefined && failure?.requestedAmount !== null;
    const hasAvailable = failure?.availableAmount !== undefined && failure?.availableAmount !== null;

    if (hasRequested && hasAvailable) {
      const requested = formatCurrency(failure.requestedAmount);
      const available = formatCurrency(failure.availableAmount);

      const translatedDetails =
        translateLiteral('market.error_funds_reservation_rejected_details', {
          requestedAmount: requested,
          availableAmount: available,
        }) ||
        translateLiteral('market.error_insufficient_funds', {
          requestedAmount: requested,
          availableAmount: available,
        });

      if (translatedDetails) {
        return translatedDetails;
      }

      return `Saldo insuficiente para presentar la oferta. Necesitas ${requested} y tienes ${available} disponibles.`;
    }

    // Si la respuesta no incluye requestedAmount o availableAmount, mostrar el literal del código
    const literalKey =
      code === 'FUNDS_RESERVATION_REJECTED'
        ? 'market.error_funds_reservation_rejected'
        : 'market.error_insufficient_funds';

    const translated = translateLiteral(literalKey);
    if (translated) {
      return translated;
    }

    return (
      MARKET_OFFER_CODE_LITERALS[code] ||
      'Reserva de fondos rechazada. Saldo insuficiente para presentar la oferta.'
    );
  }

  // Si failure.code existe, buscar si hay un literal configurado en i18n o en MARKET_OFFER_CODE_LITERALS
  if (code) {
    const literalKey = `market.error_${code.toLowerCase()}`;
    const translated = translateLiteral(literalKey);
    if (translated) {
      return translated;
    }

    if (MARKET_OFFER_CODE_LITERALS[code]) {
      return MARKET_OFFER_CODE_LITERALS[code];
    }

    return `No se pudo presentar la oferta (${code}).`;
  }

  // Respuestas HTTP 409 sin failure
  if (err?.status === 409) {
    return err?.message || 'Conflicto al presentar la oferta. Por favor, revisa las condiciones.';
  }

  // Manejo genérico de errores
  return err?.message || 'Error al enviar la oferta por el jugador.';
}

